import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { creatorImages } from "@/data/siteData";

const stats = [
  { value: "4000+", label: "creators" },
  { value: "200+", label: "campaigns" },
  { value: "100+", label: "brands" },
];

const CreatorColumn = ({ images, speed }) => {
  const list = [...images, ...images];
  return (
    <div className="flex flex-col gap-2.5 min-w-0">
      <div className={`flex flex-col gap-2.5 ${speed}`}>
        {list.map((src, i) => (
          <div
            key={i}
            className="relative overflow-hidden rounded-lg aspect-[3/4]"
          >
            <img
              src={src}
              alt=""
              loading="eager"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Hero() {
  const col1 = creatorImages.slice(0, 8);
  const col2 = creatorImages.slice(7, 15);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate overflow-hidden surface-1"
    >
      {/* Background radial washes */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-60 dark:opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(37,99,235,0.16) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-50 dark:opacity-30"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(30,58,138,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,138,0.8) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          }}
        />
      </div>

      {/* Creator scroll — lg+ only, ~25% width, 2 columns, blended full-bleed */}
      <div
        id="creators"
        data-testid="hero-creator-scroll"
        aria-hidden
        className="hidden lg:block absolute inset-y-0 right-0 w-[28%] xl:w-[26%] pointer-events-none"
      >
        <div className="relative h-full w-full overflow-hidden">
          <div className="absolute inset-0 grid grid-cols-2 gap-2.5 px-3">
            <CreatorColumn images={col1} speed="anim-scroll-up" />
            <CreatorColumn images={col2} speed="anim-scroll-down" />
          </div>
          {/* Top fade — blends into page */}
          <div className="pointer-events-none absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-white via-white/85 to-transparent dark:from-[#0B1C3D] dark:via-[#0B1C3D]/85" />
          {/* Bottom fade */}
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/85 to-transparent dark:from-[#0B1C3D] dark:via-[#0B1C3D]/85" />
          {/* Left edge soft fade */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent dark:from-[#0B1C3D]" />
        </div>
      </div>

      {/* Hero content */}
      <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-32 md:pt-36 pb-24 md:pb-28 min-h-[100vh] flex items-center">
        <div className="relative z-10 max-w-3xl lg:max-w-[680px] xl:max-w-[760px]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 px-4 py-1.5 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] anim-pulse-glow" />
            <span className="eyebrow text-blue-700 dark:text-blue-200" style={{ fontSize: "14px" }}>
              India's Leading South-Regional Creator Commerce Company
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            data-testid="hero-headline"
            className="font-heading font-bold tracking-tight text-[44px] sm:text-[56px] lg:text-[60px] xl:text-[72px] leading-[1.02] text-slate-900 dark:text-white"
          >
            We Make the Internet{" "}
            <span className="gradient-text">Talk About Your Brand</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-7 max-w-xl"
          >
            <p className="font-serif-italic text-2xl md:text-3xl text-slate-800 dark:text-slate-100 leading-snug">
              Attention is everything.
            </p>
            <p className="font-body mt-3 text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              <span className="font-medium text-slate-900 dark:text-white">Content</span>
              <span className="mx-2 text-[#2563EB]">→</span>
              <span className="font-medium text-slate-900 dark:text-white">Attention</span>
              <span className="mx-2 text-[#2563EB]">→</span>
              <span className="font-medium text-slate-900 dark:text-white">Trust</span>
              <span className="mx-2 text-[#2563EB]">→</span>
              <span className="font-medium text-slate-900 dark:text-white">Brand</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="relative mt-10 inline-flex flex-wrap items-center gap-4"
          >
            <div
              className="absolute -inset-6 -z-10 rounded-full blur-2xl opacity-60 dark:opacity-40"
              style={{
                background:
                  "radial-gradient(circle, rgba(37,99,235,0.25) 0%, transparent 70%)",
              }}
              aria-hidden
            />
            <a
              href="#contact"
              data-testid="hero-cta-book-call"
              className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] text-white px-7 py-4 text-[15px] font-semibold hover:bg-[#1E3A8A] transition-colors duration-300 shadow-[0_18px_40px_-15px_rgba(37,99,235,0.55)]"
            >
              Book a Strategy Call
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#campaigns"
              data-testid="hero-cta-view-campaigns"
              className="group inline-flex items-center gap-2 rounded-full px-7 py-4 text-[15px] font-medium text-slate-800 dark:text-white bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-300"
            >
              <Play className="w-3.5 h-3.5 text-[#2563EB]" fill="currentColor" />
              View Campaigns
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            data-testid="hero-stats-pill"
            className="mt-12 inline-flex flex-wrap items-center rounded-2xl bg-white dark:bg-white/5 ring-1 ring-slate-200 dark:ring-white/10 shadow-[0_10px_40px_-25px_rgba(15,23,42,0.2)] px-2 py-2"
          >
            {stats.map((s, i) => (
              <div key={s.label} className="flex items-center">
                <div className="px-4 md:px-6 py-1.5">
                  <span className="font-heading font-bold text-xl md:text-2xl text-slate-900 dark:text-white">
                    {s.value}
                  </span>
                  <span className="font-body ml-2 text-xs md:text-sm text-slate-500 dark:text-slate-400">
                    {s.label}
                  </span>
                </div>
                {i < stats.length - 1 && (
                  <span className="h-6 w-px bg-slate-200 dark:bg-white/10" aria-hidden />
                )}
              </div>
            ))}
          </motion.div>

          <div
            data-testid="hero-creator-overlay"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 backdrop-blur px-4 py-2"
          >
            <span className="flex -space-x-2">
              {creatorImages.slice(0, 4).map((src, i) => (
                <span key={i} className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-white dark:ring-[#0B1C3D]">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </span>
              ))}
            </span>
            <span className="font-body text-xs md:text-[13px] text-slate-700 dark:text-slate-200">
              <span className="font-semibold text-slate-900 dark:text-white">4000+ creators</span> powering your distribution
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
